import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, signal } from '@angular/core'
import { DateAdapter } from '@angular/material/core'
import { MatDialog } from '@angular/material/dialog'
import { PageEvent } from '@angular/material/paginator'
import { Sort } from '@angular/material/sort'
import { Router } from '@angular/router'
import { NewFeature, Yritysmuoto } from '@lemon/core-firebase/model/kayttaja'
import { SelvityspyynnonTyyppi, Selvityspyynto } from '@lemon/core-firebase/model/selvitettavat'
import { FirestoreTosite } from '@lemon/core-firebase/model/tosite'
import { kelpaakoAlkuperainenKansikuvaksi, onSuoraanPikkukuvaksiKelpaavaPaate } from '@lemon/core/utils/pikkukuva'
import { onSelvityspyyntoNakyvissaKayttajalle } from '@lemon/core-firebase/service/tosite/selvityspyynto-nakyvyys'
import { TositeUriService } from '@lemon/core-firebase/service/tosite/tosite-uri.service'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { DateService } from '@lemon/core/service/date.service'
import { combineLatest, firstValueFrom, Observable, of as observableOf, Subject } from 'rxjs'
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators'
import { FirebaseLemonaid } from '../../_angular/service/firebase-lemonaid.service'
import { KayttajaService } from '../../_angular/service/kayttaja.service'
import { TositeHakuStateService } from '../../_angular/service/tosite-haku-state.service'
import { TositeDatasourceUusiService, VuosiKk } from '../../_angular/service/tosite/tosite-datasource-uusi.service'
import { TositeService } from '../../_angular/service/tosite/tosite.service'
import { UserSettingsService } from '../../_angular/service/user-settings.service'
import { ProjektitService } from '../../_jaettu-angular/projektit.service'
import { DragAndDropService } from '../../_jaettu-angular/service/drag-and-drop.service'
import { LocalStorageService } from '../../_jaettu-angular/service/local-storage.service'
import { lueTositelistanEsitystapa, tallennaTositelistanEsitystapa, TositelistanEsitystapa, TositeListaProjekti, TositeListaRivi } from './tosite-lista/tosite-lista.component'
import { TositteetUudistusDialog } from './tositteet-uudistus-dialog/tositteet-uudistus.dialog'

export interface Selvitysnippu {
  tyyppi: SelvityspyynnonTyyppi
  kortit: Selvityspyynto[]
  naytettavat: Selvityspyynto[]
  auki: boolean
}

export interface MaksutapaValinta {
  tunniste: number
  nimi: string
  kuva: string | null
  lukumaara: number
  voiPudottaa: boolean
}

@Component({
  templateUrl: './tosite-uusi-listaus.component.html',
  styleUrls: ['./tosite-uusi-listaus.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TositeUusiListausComponent implements OnInit, OnDestroy {
  hakuAvoinna = false

  otsikkoObservable: Observable<string>
  valittuKuukausiObservable: Observable<VuosiKk>
  readonly edellisenKuukaudenSelvitettavat = signal(0)
  readonly seuraavanKuukaudenSelvitettavat = signal(0)

  readonly kuukaudenYhteissumma = signal('')
  readonly maksutapaValinnat = signal<MaksutapaValinta[]>([])
  readonly kaikkienLukumaara = signal(0)
  readonly valittuMaksutapa = signal<number | null>(null)
  readonly raahataan = signal(false)
  readonly pudotuskohde = signal<number | null>(null)
  private readonly _suodatettujenLukumaara = signal(0)
  readonly tositteet = signal<TositeListaRivi[]>([])
  projektit: TositeListaProjekti[] = []
  readonly projektitMap = signal(new Map<string, TositeListaProjekti>())
  nykyinenSorttaus: Sort | null = null
  esitystapa: TositelistanEsitystapa = 'kuvilla'

  readonly selvityspyynnot = signal<Selvityspyynto[]>([])
  readonly selvitysniput = signal<Selvitysnippu[]>([])

  private _avatutNiput = new Set<SelvityspyynnonTyyppi>()
  private readonly _yritysmuoto = signal<Yritysmuoto | null>(null)

  private _ngUnsubscribe = new Subject<void>()

  get datasourceService(): TositeDatasourceUusiService {
    return this._datasourceService
  }

  constructor(
    private _hakuStateService: TositeHakuStateService,
    private _datasourceService: TositeDatasourceUusiService,
    private _tositeService: TositeService,
    private _tositeUriService: TositeUriService,
    private _router: Router,
    private _projektitService: ProjektitService,
    private _currencyService: CurrencyService,
    private _dateService: DateService,
    private _dateAdapter: DateAdapter<Date>,
    private _kayttajaService: KayttajaService,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _userSettingsService: UserSettingsService,
    private _dialog: MatDialog,
    private _localStorageService: LocalStorageService,
    private _dndService: DragAndDropService,
  ) {}

  ngOnInit() {
    this._datasourceService.changeTositeTyyppi('osto')
    this.nykyinenSorttaus = this._datasourceService.getSort()
    this.esitystapa = lueTositelistanEsitystapa(this._localStorageService)
    this._naytaUudistusDialogiKertaalleen()

    if (this._hakuStateService.onkoTallennettuHaku(this._router.url)) {
      this.hakuAvoinna = true
    }

    this.otsikkoObservable = this._datasourceService.hakukriteeritObservable.pipe(
      map(kriteerit => {
        const kuukausi = this._dateAdapter.getMonthNames('long')[kriteerit.vuosikk.kk]
        return `${kuukausi} ${kriteerit.vuosikk.vuosi}`
      }),
    )

    this.valittuKuukausiObservable = this._datasourceService.hakukriteeritObservable.pipe(map(kriteerit => kriteerit.vuosikk))

    this._projektitService.naytettavatKirjanpitoprojektitObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(projektit => {
      this.projektit = projektit.map(p => ({
        id: p.avain,
        nimi: p.nimi,
        ...this._projektitService.annaProjektinVarit(p.avain),
      }))
      this.projektitMap.set(new Map(this.projektit.map(p => [p.id, p])))
    })

    this._kayttajaService.nykyinenAsiakasObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(asiakas => {
      this._yritysmuoto.set(asiakas?.yritysmuoto ?? null)
    })

    const kaikkiSelvityspyynnot$ = this._kayttajaService.kayttajanTiedotObservable.pipe(
      switchMap(kt => {
        if (!kt?.asiakasAvain) {
          return observableOf<Selvityspyynto[]>([])
        }
        const polku = this._tositeUriService.annaSelvityspyynnotCollectionUri(kt.asiakasAvain)
        return this._firebaseLemonaid.firestoreCollection<Selvityspyynto>(polku).listen()
      }),
    )

    const nakyvatSelvityspyynnot$ = combineLatest([kaikkiSelvityspyynnot$, this._kayttajaService.kayttajaObservable]).pipe(
      map(([pyynnot, kayttaja]) => {
        if (!kayttaja) {
          return []
        }
        return (pyynnot ?? []).filter(p => p.tila === 'avoin' && p.poistettu !== true && onSelvityspyyntoNakyvissaKayttajalle(kayttaja, p))
      }),
    )

    combineLatest([nakyvatSelvityspyynnot$, this._datasourceService.hakukriteeritObservable])
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(([kaikki, hakukriteerit]) => {
        const vuosi = hakukriteerit.vuosikk.vuosi
        const kuukausi = hakukriteerit.vuosikk.kk + 1
        const valittuKuukausi = vuosi * 100 + kuukausi

        this.selvityspyynnot.set(kaikki.filter(p => p.pvml?.year === vuosi && p.pvml?.month === kuukausi))
        this.edellisenKuukaudenSelvitettavat.set(kaikki.filter(p => p.pvml && p.pvml.year * 100 + p.pvml.month < valittuKuukausi).length)
        this.seuraavanKuukaudenSelvitettavat.set(kaikki.filter(p => p.pvml && p.pvml.year * 100 + p.pvml.month > valittuKuukausi).length)

        this._paivitaSelvitysniput()
      })

    combineLatest([this._datasourceService.kuukaudenKuititObservable, this._tositeService.maksutavatObservable, this._datasourceService.valittuMaksutapaObservable])
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(([kuitit, maksutavat, valittuMaksutapa]) => {
        const lukumaarat = new Map<number, number>()
        for (const kuitti of kuitit) {
          lukumaarat.set(kuitti.maksutapa, (lukumaarat.get(kuitti.maksutapa) ?? 0) + 1)
        }
        this.valittuMaksutapa.set(valittuMaksutapa)
        this.kaikkienLukumaara.set(kuitit.length)
        this.maksutapaValinnat.set(
          (maksutavat ?? [])
            .filter(mt => mt.a || lukumaarat.has(mt.i) || mt.i === valittuMaksutapa)
            .map(mt => ({
              tunniste: mt.i,
              nimi: mt.l,
              kuva: mt.img ? `data:image/png;base64,${mt.img}` : null,
              lukumaara: lukumaarat.get(mt.i) ?? 0,
              voiPudottaa: mt.a,
            })),
        )
      })

    this._dndService.isDraggingInWindowObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(raahataan => {
      this.raahataan.set(raahataan)
      if (!raahataan) {
        this.pudotuskohde.set(null)
      }
    })

    this._datasourceService.suodatetutKuititObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(kuitit => {
      this._suodatettujenLukumaara.set(kuitit.length)
      this.kuukaudenYhteissumma.set(this._laskeYhteissumma(kuitit.map(k => k.sum ?? 0)))
    })

    combineLatest([this._datasourceService.kuititObservable, this._tositeService.maksutavatObservable, this._kayttajaService.kayttajanTiedotObservable])
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(([kuitit, maksutavat, kayttajanTiedot]) => {
        const lista = kuitit ?? []
        this.tositteet.set(
          lista.map(kuitti => ({
            id: kuitti.avain,
            paivamaara: kuitti.p ?? null,
            toimittajanNimi: kuitti.seller ?? kuitti.el ?? null,
            summa: kuitti.sum ?? null,
            maksutapa: maksutavat?.find(mt => mt.i === kuitti.maksutapa) ?? null,
            lisatiedot: kuitti.selite ?? '',
            projektinId: kuitti.projektiAvain ?? null,
            kasittelyKesken: this._onkoKasittelyKesken(kuitti),
            kansikuvaUrl: kayttajanTiedot?.asiakasId ? this._annaKansikuvaUrl(kuitti, kayttajanTiedot.asiakasId) : null,
            kuvia: this._laskeKuvat(kuitti),
          })),
        )
      })
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  private _naytaUudistusDialogiKertaalleen() {
    this._kayttajaService.kayttajanTiedotObservable
      .pipe(
        filter(kayttajanTiedot => !!kayttajanTiedot?.uid && !!kayttajanTiedot?.asiakasAvain),
        switchMap(kayttajanTiedot =>
          this._userSettingsService.userSettingsObservable.pipe(
            filter(asetukset => asetukset !== null),
            map(asetukset => ({ kayttajanTiedot, nahty: !!asetukset?.seen?.[NewFeature.TOSITTEET_UUDISTUS] })),
          ),
        ),
        take(1),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(async ({ kayttajanTiedot, nahty }) => {
        if (nahty) {
          return
        }
        const dialogRef = this._dialog.open(TositteetUudistusDialog, { autoFocus: false })
        await firstValueFrom(dialogRef.afterClosed())
        try {
          await this._userSettingsService.setFeatureSeen(NewFeature.TOSITTEET_UUDISTUS, kayttajanTiedot.asiakasAvain, kayttajanTiedot.uid, this._firebaseLemonaid)
        } catch (err) {
          console.error('Tositteet-uudistuksen nähdyksi merkitseminen epäonnistui', err)
        }
      })
  }

  private _annaKansikuvaUrl(kuitti: FirestoreTosite, asiakasId: string) {
    if (!kuitti.kuvakansio) {
      return null
    }
    const alkuperaiset = kuitti.alkuperaiset ?? {}
    const kuva = Object.entries(kuitti.kuvat ?? {})
      .filter(([, k]) => k && k.poistettu !== true)
      .filter(([, k]) => !k.alkuperaisenAvain || (alkuperaiset[k.alkuperaisenAvain] && alkuperaiset[k.alkuperaisenAvain].poistettu !== true))
      .filter(([, k]) => onSuoraanPikkukuvaksiKelpaavaPaate(k.type ?? 'jpg'))
      .sort(([, a], [, b]) => (a.jarjestys ?? 0) - (b.jarjestys ?? 0))[0]
    if (kuva) {
      const [avain, tiedot] = kuva
      return `/api/1/kuitit/kuvat/thumb/${this._tositeUriService.annaCloudStorageKuvaUri(asiakasId, kuitti.kuvakansio, tiedot.avain ?? avain, tiedot.type ?? 'jpg')}`
    }
    const alkuperainen = Object.entries(alkuperaiset)
      .filter(([, a]) => a && a.poistettu !== true)
      .filter(([, a]) => kelpaakoAlkuperainenKansikuvaksi(a.fileEnding ?? 'jpg', a.kasitelty === true))
      .sort(([, a], [, b]) => (a.jarjestys ?? 0) - (b.jarjestys ?? 0))[0]
    if (alkuperainen) {
      const [avain, tiedot] = alkuperainen
      return `/api/1/kuitit/kuvat/thumb/${this._tositeUriService.annaCloudStorageAlkuperainenUri(asiakasId, tiedot.kuvakansio ?? kuitti.kuvakansio, tiedot.avain ?? avain, tiedot.fileEnding ?? 'jpg')}`
    }
    return null
  }

  private _laskeKuvat(kuitti: FirestoreTosite) {
    const alkuperaisia = Object.values(kuitti.alkuperaiset ?? {}).filter(a => a && a.poistettu !== true).length
    const irrallisiaKuvia = Object.values(kuitti.kuvat ?? {}).filter(k => k && k.poistettu !== true && !k.alkuperaisenAvain).length
    return alkuperaisia + irrallisiaKuvia
  }

  vaihdaEsitystapa(esitystapa: TositelistanEsitystapa) {
    this.esitystapa = esitystapa
    tallennaTositelistanEsitystapa(this._localStorageService, esitystapa)
  }

  private _onkoKasittelyKesken(kuitti: FirestoreTosite): boolean {
    if (!kuitti.alkuperaiset) {
      return false
    }
    for (const alkuperaisenAvain of Object.keys(kuitti.alkuperaiset)) {
      const alkuperainen = kuitti.alkuperaiset[alkuperaisenAvain]
      if (!alkuperainen.kasitelty) {
        return true
      }
    }
    return false
  }

  get tositteidenKokonaismaara(): number {
    if (this.valittuMaksutapa() !== null) {
      return this._suodatettujenLukumaara()
    }
    return this._suodatettujenLukumaara() + this.selvityspyynnot().length
  }

  valitseMaksutapa(maksutapa: number | null) {
    this._datasourceService.vaihdaValittuMaksutapa(maksutapa)
  }

  naytetaankoValittuna(valinta: MaksutapaValinta) {
    if (this.raahataan() && valinta.voiPudottaa) {
      return this.pudotuskohde() === valinta.tunniste
    }
    return this.valittuMaksutapa() === valinta.tunniste
  }

  kasitteleRaahausMaksutavanYli(event: DragEvent, valinta: MaksutapaValinta) {
    if (!valinta.voiPudottaa) {
      return
    }
    event.preventDefault()
    this.pudotuskohde.set(valinta.tunniste)
  }

  kasitteleRaahausPoisMaksutavalta(valinta: MaksutapaValinta) {
    if (this.pudotuskohde() === valinta.tunniste) {
      this.pudotuskohde.set(null)
    }
  }

  kasittelePudotusMaksutavalle(event: DragEvent, valinta: MaksutapaValinta) {
    event.preventDefault()
    this.pudotuskohde.set(null)
    const tiedostot = Array.from(event.dataTransfer?.files ?? [])
    if (tiedostot.length === 0) {
      return
    }
    this._router.navigate(['/tositteet/osto/lisaa'], { state: { pudotetutTiedostot: tiedostot, pakotettuMaksutapa: valinta.tunniste } })
  }

  avaaHaku() {
    this.hakuAvoinna = true
  }

  suljeHaku() {
    this.hakuAvoinna = false
  }

  avaaTosite(tositteenAvain: string) {
    this._hakuStateService.merkitseUusiListauksestaTulo()
    this._router.navigate(['/tositteet', 'osto', tositteenAvain])
  }

  sorttausMuuttui(sort: Sort) {
    this.nykyinenSorttaus = sort
    this._datasourceService.changeSort(sort)
  }

  sivunvaihto(event: PageEvent) {
    this._datasourceService.changePage(event)
  }

  edellinenKuukausi() {
    const vuosiKk = this._datasourceService.getVuosiKk()
    const vuosi = vuosiKk.kk < 1 ? vuosiKk.vuosi - 1 : vuosiKk.vuosi
    const kuukausi = vuosiKk.kk < 1 ? 11 : vuosiKk.kk - 1
    this._datasourceService.changeVuosiKk(vuosi, kuukausi)
  }

  seuraavaKuukausi() {
    const vuosiKk = this._datasourceService.getVuosiKk()
    const vuosi = vuosiKk.kk > 10 ? vuosiKk.vuosi + 1 : vuosiKk.vuosi
    const kuukausi = vuosiKk.kk > 10 ? 0 : vuosiKk.kk + 1
    this._datasourceService.changeVuosiKk(vuosi, kuukausi)
  }

  valitseKuukausi(vuosiKk: VuosiKk) {
    this._datasourceService.changeVuosiKk(vuosiKk.vuosi, vuosiKk.kk)
  }

  avaaSelvitysnippu(tyyppi: SelvityspyynnonTyyppi) {
    this._avatutNiput.add(tyyppi)
    this._paivitaSelvitysniput()
  }

  niputaSelvitettavat(tyyppi: SelvityspyynnonTyyppi) {
    this._avatutNiput.delete(tyyppi)
    this._paivitaSelvitysniput()
  }

  private _paivitaSelvitysniput() {
    const jarjestetyt = [...this.selvityspyynnot()].sort((a, b) => (this._dateService.localDateToNumber(b.pvml) ?? 0) - (this._dateService.localDateToNumber(a.pvml) ?? 0))
    const niput: Selvitysnippu[] = []
    for (const pyynto of jarjestetyt) {
      const tyyppi: SelvityspyynnonTyyppi = pyynto.tyyppi === 'kuititon' ? 'kuititon' : 'lisatietopyynto'
      const nippu = niput.find(n => n.tyyppi === tyyppi)
      if (nippu) {
        nippu.kortit.push(pyynto)
      } else {
        niput.push({ tyyppi, kortit: [pyynto], naytettavat: [], auki: false })
      }
    }
    for (const nippu of niput) {
      if (nippu.kortit.length < 2) {
        this._avatutNiput.delete(nippu.tyyppi)
      }
      nippu.auki = this._avatutNiput.has(nippu.tyyppi)
      nippu.naytettavat = nippu.auki ? nippu.kortit : nippu.kortit.slice(0, 1)
    }
    this.selvitysniput.set(niput)
  }

  get yritysmuoto(): Yritysmuoto | null {
    return this._yritysmuoto()
  }

  private _laskeYhteissumma(summat: number[]): string {
    if (summat.length === 0) {
      return ''
    }
    const yhteensa = summat.reduce((acc, n) => acc + n, 0)
    return this._currencyService.formatoiRaha(yhteensa, 'EUR', 'fi')
  }
}
